/***
 *
 *   USER
 *   Shows the current user name and avatar.
 *   If user belongs to more than one account, they can switch accounts here.
 *
 *   DOCS
 *   https://docs.useMapMetrics Atlas.app/MapMetrics Atlas-web/components/user
 *
 **********/

import { Fragment, useContext, useCallback } from "react";
import Axios from "axios";
import i18n from "i18next";

import {
  AuthContext,
  Avatar,
  Button,
  ViewContext,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSubContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "components/lib";

import { 
  GB, ES, DE, FR, IT, PT, NL, PL, SE, DK, FI, NO, CZ, HU, RO, GR,
  CN, JP, KR, RU, IN, SA
} from "country-flag-icons/react/3x2";
import Settings from "settings.json";

export function User() {
  // context
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);

  const flags = { 
    en: GB, es: ES, de: DE, fr: FR, it: IT, pt: PT, nl: NL, pl: PL, 
    sv: SE, da: DK, fi: FI, no: NO, cs: CZ, hu: HU, ro: RO, el: GR,
    zh: CN, ja: JP, ko: KR, ru: RU, hi: IN, ar: SA
  };
  const Locales = Settings[process.env.NODE_ENV].locales;
  const darkMode = authContext.user.dark_mode;

  const changeLocale = useCallback(
    (locale) => {
      Axios.defaults.headers.common["Accept-Language"] = locale;
      i18n.changeLanguage(locale);
      authContext.update({ locale });
      localStorage.setItem("user_locale", locale);
    },
    [i18n, authContext]
  );
  const toggleDarkMode = useCallback(async () => {
    const newDarkMode = !darkMode;

    authContext.update({ dark_mode: newDarkMode });

    newDarkMode
      ? document.getElementById("app").classList.add("dark")
      : document.getElementById("app").classList.remove("dark");

    localStorage.setItem("dark_mode", JSON.stringify(newDarkMode));

    // Trigger a synthetic storage event to notify other components
    window.dispatchEvent(new Event("storage"));

    await Axios.patch("/api/user", { dark_mode: newDarkMode });
  }, [darkMode, authContext]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar
          src={authContext.user?.avatar}
          fallback={authContext.user?.name?.charAt(0) || '?'}
          className="w-7 h-7 md:w-9 md:h-9 cursor-pointer"
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {authContext.user?.accounts?.length > 1 && (
          <Fragment>
            <DropdownMenuLabel>Switch Account</DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuGroup>
              {authContext.user?.accounts?.map((account) => {
                return (
                  <DropdownMenuItem key={account.id}>
                    <span
                      onClick={() => {
                        viewContext.setLoading(true);
                        authContext.switchAccount(account.id);
                      }}
                    >
                      {account.name}
                    </span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>

            <DropdownMenuSeparator />
          </Fragment>
        )}

        <DropdownMenuItem>
          <Button
            variant="naked"
            icon="user"
            text="Edit Account"
            url="/account/profile"
            size="full"
            className="justify-start"
          />
        </DropdownMenuItem>

        {Locales && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Button
                variant="naked"
                icon="globe"
                text="Language"
                size="full"
                className="justify-start"
              />
            </DropdownMenuSubTrigger>

            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {Locales.map((locale) => {
                  const Flag = flags[locale.value];

                  return (
                    <DropdownMenuCheckboxItem
                      key={locale.value}
                      checked={i18n.resolvedLanguage === locale.value}
                    >
                      <Flag className="w-4 self-center mr-2" />

                      <Button
                        variant="naked"
                        text={locale.label}
                        action={() => changeLocale(locale.value)}
                      />
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}

        <DropdownMenuItem>
          <Button
            variant="naked"
            icon="help-circle"
            text="Help"
            url="/help"
            size="full"
            className="justify-start"
          />
        </DropdownMenuItem>

        <DropdownMenuItem>
          <Button
            variant="naked"
            icon="file-text"
            text="Terms of Use"
            url="/terms"
            size="full"
            className="justify-start"
          />
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem>
          <Button
            text="Signout"
            icon="log-out"
            variant="naked"
            size="full"
            className="justify-start"
            action={authContext.signout}
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
